import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, Dialog, IconButton, TextField } from '@mui/material';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import loadStripeJs from '../../../utils/loadStripe';
import {
    CheckoutElementsProvider,
    CurrencySelectorElement,
    PaymentElement,
    useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';

import { createDqtWireOrderService, uploadDqtWireProofService } from '../../../services/billing.service';
import { useColorMode } from '../../../context/colorMode/colorMode.context';

/**
 * DqtCheckoutDialog — "Make a Payment": pay for a plan or a DQ Token top-up by
 * card (Stripe Checkout Elements) or by bank transfer.
 *
 * Card: a Checkout Session with ui_mode 'elements', created by the parent before the
 * dialog opens. Adaptive Pricing: Stripe shows the price in the buyer's local
 * currency when it can, and REQUIRES the Currency Selector Element on the page and
 * `adaptivePricing.allowed` on the provider. Wallets (Apple Pay, Google Pay, Link)
 * are shown only when an administrator has turned them on; the server makes the
 * session card only otherwise, and the Payment Element hides the card wallets.
 *
 * Bank transfer (shown only when an administrator offers it): choosing it opens the
 * order's invoice on the server, whose number is the reference to quote. After
 * paying, the buyer uploads proof; DQ reviews it and approval adds the DQ Tokens.
 *
 * Nothing is granted by this dialog. `onPaid(sessionId)` tells the parent a card
 * payment was confirmed; the parent asks the server, which reads the session from
 * Stripe and grants the DQT. `onWireSubmitted(order)` tells it proof was sent.
 */

// Themeable via CSS custom properties, same convention as the sign-up
// SignupFields.comp — a host page can retint this dialog (it renders through a
// MUI Dialog portal to document.body, so the host sets these on
// document.documentElement rather than a page-root inline style; see
// PoweredByDQ/Chatbot.page.jsx). A page that sets none keeps this exact hex.
const ORANGE = 'var(--pbdq-accent, #E8580C)';
const ORANGE_HOVER = 'var(--pbdq-accent-hover, #C2410C)';
const ORANGE_SOFT = 'var(--pbdq-accent-tint, #FFF4ED)';
// The tint's dark-mode stand-in: dark mode lightens text, so a pale tint behind it
// would leave light text on a light box.
const ORANGE_SOFT_DARK = 'rgba(232,88,12,0.14)';
const BORDER = 'var(--brand-border, #E5E7EB)';

// The resolved value of --pbdq-accent, for the one place (Stripe Elements'
// `appearance.variables`) that needs an actual color rather than a CSS var.
const resolveAccentColor = () => {
    if (typeof window === 'undefined') return '#E8580C';
    const value = getComputedStyle(document.documentElement).getPropertyValue('--pbdq-accent').trim();
    return value || '#E8580C';
};
const MUTED = 'var(--brand-text-muted, #6B7280)';

// loadStripe must run once per key, outside render.
const stripePromises = {};
const stripeFor = (publishableKey) => {
    if (!stripePromises[publishableKey]) stripePromises[publishableKey] = loadStripeJs(publishableKey);
    return stripePromises[publishableKey];
};

const fmtUsd = (n) => `USD ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDqt = (n) => `${Number(n || 0).toLocaleString('en-US')} DQT`;

const PROOF_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const PROOF_MAX_BYTES = 10 * 1024 * 1024;

/** A courtesy check before uploading — the server repeats every one of these. */
const rejectProofFile = (file) => {
    if (!file) return 'Choose a file.';
    const parts = String(file.name || '').split('.');
    const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    if (!PROOF_EXTENSIONS.includes(ext)) return 'Upload the proof as a PDF, JPG or PNG file.';
    if (file.size > PROOF_MAX_BYTES) return 'The file is larger than 10 MB.';
    return null;
};

// ─── Building blocks ─────────────────────────────────────────────────────────

const SectionTitle = ({ children, sx }) => (
    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--brand-text, #111827)', ...sx }}>{children}</Typography>
);

const Banner = ({ tone = 'orange', icon, children, sx }) => {
    const { isDark } = useColorMode();
    const tones = isDark ? {
        orange: { bg: ORANGE_SOFT_DARK, border: 'rgba(232,88,12,0.4)', color: '#FDBA74' },
        blue:   { bg: 'rgba(59,130,246,0.14)', border: 'rgba(59,130,246,0.4)', color: '#93C5FD' },
        amber:  { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)', color: '#FCD34D' },
    } : {
        orange: { bg: ORANGE_SOFT, border: '#FBD5BF', color: ORANGE_HOVER },
        blue:   { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E3A8A' },
        amber:  { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E' },
    };
    const t = tones[tone];
    return (
        <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, borderRadius: '8px', background: t.bg, border: `1px solid ${t.border}`, color: t.color, ...sx }}>
            <Box sx={{ pt: '1px' }}>{icon || <InfoOutlinedIcon style={{ fontSize: 18 }}/>}</Box>
            <Box sx={{ fontSize: '0.82rem', lineHeight: 1.55, flex: 1 }}>{children}</Box>
        </Box>
    );
};

const BrandBadge = ({ label, color }) => (
    <Box sx={{ px: 1, py: 0.4, border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: 11, fontWeight: 800, color, background: '#fff', letterSpacing: '0.3px' }}>
        {label}
    </Box>
);

const MethodCard = ({ selected, onSelect, icon, title, text, children }) => {
    const { isDark } = useColorMode();
    return (
    <Box
        role="radio" aria-checked={selected} tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
        sx={{
            flex: '1 1 260px', p: 2.25, borderRadius: '10px', cursor: 'pointer', position: 'relative',
            border: `1.5px solid ${selected ? ORANGE : BORDER}`,
            background: selected ? (isDark ? ORANGE_SOFT_DARK : ORANGE_SOFT) : 'var(--brand-surface, #fff)',
            transition: 'border-color .15s, background .15s',
            '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: 2 },
        }}
    >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{
                width: 18, height: 18, mt: 0.25, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${selected ? ORANGE : '#9CA3AF'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: ORANGE }}/>}
            </Box>
            <Box sx={{ color: ORANGE, mt: -0.25 }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.98rem' }}>{title}</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: MUTED, mt: 0.5, lineHeight: 1.5 }}>{text}</Typography>
            </Box>
        </Box>
        {children}
    </Box>
    );
};

const SummaryRow = ({ label, value, strong }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: strong ? 0 : 0.75 }}>
        <Typography sx={{ fontSize: strong ? '0.95rem' : '0.85rem', fontWeight: strong ? 700 : 400, color: strong ? 'var(--brand-text, #111827)' : MUTED }}>{label}</Typography>
        <Typography sx={{ fontSize: strong ? '1.15rem' : '0.85rem', fontWeight: strong ? 800 : 500, color: strong ? ORANGE : 'var(--brand-text, #374151)' }}>{value}</Typography>
    </Box>
);

/**
 * `lines`: a partner's sign-up invoice — the plan once and its first year of access —
 * [{ key, description, billing: 'one_time' | 'annual', amount_usd }]. Shown above the
 * subtotal; an order with one line passes none.
 */
const PaymentSummary = ({ subtotal, tax, total, receive, lines = [] }) => (
    <Box sx={{ p: 2.25, borderRadius: '10px', border: `1px solid ${BORDER}`, background: 'var(--brand-bg, #F9FAFB)' }}>
        <SectionTitle sx={{ fontSize: '0.95rem', mb: 1.5 }}>Payment Summary</SectionTitle>
        {lines.length > 1 && (
            <>
                {lines.map((l) => (
                    <SummaryRow key={l.key} label={l.description}
                        value={`${fmtUsd(l.amount_usd)}${l.billing === 'annual' ? ' / year' : ''}`}/>
                ))}
                <Box sx={{ borderTop: `1px solid ${BORDER}`, my: 1 }}/>
            </>
        )}
        <SummaryRow label="Subtotal" value={subtotal}/>
        <SummaryRow label="Tax (0%)" value={tax}/>
        {receive && <SummaryRow label="You receive" value={receive}/>}
        <Box sx={{ borderTop: `1px solid ${BORDER}`, my: 1.5 }}/>
        <SummaryRow label="Total Due" value={total} strong/>
    </Box>
);

const primaryButtonSx = {
    backgroundColor: ORANGE, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '8px',
    px: 3, py: 1.1, boxShadow: 'none', '&:hover': { backgroundColor: ORANGE_HOVER, boxShadow: 'none' },
};
const secondaryButtonSx = {
    textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, py: 1.1,
    color: 'var(--brand-text, #374151)', borderColor: BORDER,
};

const Footer = ({ left, children }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
        px: { xs: 2, sm: 3.5 }, py: 2, borderTop: `1px solid ${BORDER}`, background: 'var(--brand-surface, #fff)',
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>{left}</Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>{children}</Box>
    </Box>
);

const TwoColumns = ({ left, right }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.35fr 1fr' }, gap: 3 }}>
        <Box sx={{ minWidth: 0 }}>{left}</Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{right}</Box>
    </Box>
);

// ─── Card ────────────────────────────────────────────────────────────────────

const NEW_CARD = 'new';

const cardBrand = (brand) => {
    const b = String(brand || 'card');
    return b === 'amex' ? 'American Express' : b.charAt(0).toUpperCase() + b.slice(1);
};

/** One radio row: a saved card, or "Use a different card". */
const SavedCardRow = ({ selected, onSelect, title, text, badge }) => {
    const { isDark } = useColorMode();
    return (
        <Box
            role="radio" aria-checked={selected} tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.25, borderRadius: '8px', cursor: 'pointer',
                border: `1.5px solid ${selected ? ORANGE : BORDER}`,
                background: selected ? (isDark ? ORANGE_SOFT_DARK : ORANGE_SOFT) : 'var(--brand-surface, #fff)',
                '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: 2 },
            }}
        >
            <Box sx={{
                width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${selected ? ORANGE : '#9CA3AF'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {selected && <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: ORANGE }}/>}
            </Box>
            <CreditCardIcon style={{ fontSize: 22, color: MUTED }}/>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{title}</Typography>
                {text && <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>{text}</Typography>}
            </Box>
            {badge && (
                <Box sx={{ px: 1, py: 0.25, borderRadius: '999px', fontSize: 11, fontWeight: 700, color: ORANGE_HOVER, border: `1px solid ${ORANGE}` }}>
                    {badge}
                </Box>
            )}
        </Box>
    );
};

/**
 * Card payment. When the workspace has saved cards (Settings → Payment methods, sent
 * with the checkout as `saved_cards`, primary first), they are listed and the primary
 * one is preselected, so the buyer can pay without typing the card again; "Use a
 * different card" shows the Payment Element. A saved card is paid by passing its id
 * to confirm() — Stripe accepts only a card attached to the session's customer.
 * Checkout's own `savedPaymentMethods` is not used: it came back empty for cards
 * saved through a SetupIntent.
 */
const CardPayment = ({ item, walletsEnabled, savedCards = [], fallbackEmail, receiveText, onPaid, onCancel }) => {
    const state = useCheckoutElements();
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    // null until the buyer picks: the primary card, else the first saved one, is the default.
    const [picked, setPicked] = useState(null);

    if (state.type === 'loading') {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress style={{ color: ORANGE }}/></Box>;
    }
    if (state.type === 'error') {
        return <Box sx={{ p: 3 }}><Typography sx={{ color: '#dc2626' }}>Could not load the payment form: {state.error.message}</Typography></Box>;
    }

    const { checkout } = state;
    const total = checkout.total?.total?.amount || fmtUsd(item.amount_usd);
    const subtotal = checkout.total?.subtotal?.amount || total;
    const tax = checkout.total?.taxExclusive?.amount || checkout.total?.taxInclusive?.amount || '0.00';

    // The server sends the primary card first.
    const chosen = picked && (picked === NEW_CARD || savedCards.some((c) => c.payment_method_id === picked))
        ? picked
        : (savedCards[0]?.payment_method_id || NEW_CARD);
    const useSaved = chosen !== NEW_CARD;

    const handlePay = async () => {
        if (submitting) return;
        setSubmitting(true);
        setMessage('');
        const result = await checkout.confirm({
            // Cards finish here; redirect-based methods go to the session's
            // return_url and are picked up by the page from the URL.
            redirect: 'if_required',
            ...(useSaved ? { paymentMethod: chosen } : {}),
            ...(checkout.email ? {} : { email: fallbackEmail }),
        });
        if (result.type === 'error') {
            setMessage(result.error.message || 'The payment could not be completed.');
            setSubmitting(false);
            return;
        }
        onPaid(result.session?.id || checkout.id);
    };

    return (
        <>
            <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 3 }}>
                <TwoColumns
                    left={(
                        <>
                            <SectionTitle sx={{ mb: 1.5 }}>Pay with Card</SectionTitle>
                            {/* Required by Stripe for Adaptive Pricing with Elements — above the Payment Element. */}
                            <Box sx={{ mb: 2 }}><CurrencySelectorElement/></Box>
                            {savedCards.length > 0 && (
                                <Box role="radiogroup" aria-label="Saved cards" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                    {savedCards.map((c) => (
                                        <SavedCardRow
                                            key={c.payment_method_id}
                                            selected={chosen === c.payment_method_id} onSelect={() => setPicked(c.payment_method_id)}
                                            title={`${cardBrand(c.brand)} •••• ${c.last4 || ''}`}
                                            text={c.exp_month ? `Expires ${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}` : null}
                                            badge={c.is_default ? 'Primary' : null}
                                        />
                                    ))}
                                    <SavedCardRow
                                        selected={chosen === NEW_CARD} onSelect={() => setPicked(NEW_CARD)}
                                        title="Use a different card"
                                    />
                                </Box>
                            )}
                            {/* Kept mounted while a saved card is chosen, so details typed in it survive switching back. */}
                            <Box sx={{ display: useSaved ? 'none' : 'block' }}>
                                <PaymentElement options={{
                                    layout: 'tabs',
                                    wallets: walletsEnabled ? { applePay: 'auto', googlePay: 'auto' } : { applePay: 'never', googlePay: 'never' },
                                }}/>
                            </Box>
                            {message && <Typography sx={{ mt: 1.5, fontSize: '0.85rem', color: '#dc2626' }}>{message}</Typography>}
                        </>
                    )}
                    right={(
                        <>
                            <PaymentSummary subtotal={subtotal} tax={tax} total={total} receive={receiveText} lines={item.lines || []}/>
                            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 0.5 }}>
                                <LockOutlinedIcon style={{ fontSize: 20, color: ORANGE }}/>
                                <Typography sx={{ fontSize: '0.82rem', color: MUTED, lineHeight: 1.55 }}>
                                    Your payment information is secure and encrypted. Prices are set in US dollars;
                                    if you pay in another currency, Stripe converts the price at checkout.
                                </Typography>
                            </Box>
                        </>
                    )}
                />
            </Box>
            <Footer left={(
                <>
                    <VerifiedUserIcon style={{ fontSize: 30, color: ORANGE }}/>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Secure Checkout</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>We use industry-standard encryption to protect your data.</Typography>
                    </Box>
                </>
            )}>
                <Button variant="outlined" onClick={onCancel} disabled={submitting} sx={secondaryButtonSx}>Cancel</Button>
                <Button
                    variant="contained" onClick={handlePay} disabled={submitting || (!useSaved && !checkout.canConfirm)}
                    startIcon={submitting ? null : <LockOutlinedIcon style={{ fontSize: 18 }}/>}
                    sx={primaryButtonSx}
                >
                    {submitting ? <CircularProgress size={18} style={{ color: '#fff' }}/> : `Pay ${total}`}
                </Button>
            </Footer>
        </>
    );
};

// ─── Bank transfer ───────────────────────────────────────────────────────────

const BankRow = ({ label, value, note }) => (value ? (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 12px 1fr', py: 0.6, fontSize: '0.8rem' }}>
        <Typography sx={{ fontSize: 'inherit', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ fontSize: 'inherit', color: MUTED }}>:</Typography>
        <Typography sx={{ fontSize: 'inherit', wordBreak: 'break-word' }}>
            {value}{note ? <span style={{ color: MUTED }}> ({note})</span> : null}
        </Typography>
    </Box>
) : null);

/**
 * `createWire` / `uploadProof` replace the signed-in services — the public sign-up
 * page pays for an application, not for the caller's workspace.
 */
const WirePayment = ({ item, payload, receiveText, onCancel, onWireSubmitted, initialWire = null, initialStep = 'instructions', createWire = null, uploadProof = null }) => {
    const { isDark } = useColorMode();
    const [loading, setLoading] = useState(!initialWire);
    const [failed, setFailed] = useState(false);
    const [wire, setWire] = useState(initialWire);  // { order, instructions }
    const [step, setStep] = useState(initialStep);   // 'instructions' | 'proof' | 'submitted'
    const [file, setFile] = useState(null);
    const [note, setNote] = useState('');
    const [fileError, setFileError] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef(null);

    useEffect(() => {
        if (initialWire) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await (createWire ? createWire() : createDqtWireOrderService(payload));
                if (!cancelled) {
                    if (res?.data?.order) setWire(res.data); else setFailed(true);
                }
            } catch (_) {
                // invokeApi has already shown the server's reason.
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // The payload is fixed for the life of the dialog.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFile = (e) => {
        const chosen = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!chosen) return;
        const why = rejectProofFile(chosen);
        setFileError(why || '');
        setFile(why ? null : chosen);
    };

    const handleSubmitProof = async () => {
        if (!file || uploading) return;
        setUploading(true);
        try {
            const res = await (uploadProof
                ? uploadProof(wire.order.order_id, file, note.trim())
                : uploadDqtWireProofService(wire.order.order_id, file, note.trim()));
            if (res?.data?.order) {
                setWire((w) => ({ ...w, order: res.data.order }));
                setStep('submitted');
                if (onWireSubmitted) onWireSubmitted(res.data.order);
            }
        } catch (_) {
            // invokeApi has already shown the server's reason.
        } finally {
            setUploading(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress style={{ color: ORANGE }}/></Box>;
    if (failed || !wire) {
        return (
            <>
                <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 3 }}>
                    <Typography sx={{ color: '#dc2626' }}>Bank transfer details could not be prepared. Please try again, or pay by card.</Typography>
                </Box>
                <Footer left={null}><Button variant="outlined" onClick={onCancel} sx={secondaryButtonSx}>Close</Button></Footer>
            </>
        );
    }

    const { order, instructions: bank } = wire;
    const reference = order.invoice_no;
    const help = [bank.support_email, bank.support_phone].filter(Boolean).join(' or ');
    const needHelp = help ? (
        <>
            <HeadsetMicIcon style={{ fontSize: 30, color: ORANGE }}/>
            <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Need Help?</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>Contact our support team at {help}</Typography>
            </Box>
        </>
    ) : null;

    const summary = (
        <>
            <PaymentSummary subtotal={fmtUsd(item.amount_usd)} tax={fmtUsd(0)} total={fmtUsd(item.amount_usd)} receive={receiveText} lines={item.lines || []}/>
            {bank.processing_note && (
                <Banner tone="amber" sx={{ flexDirection: 'row' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>Important</Typography>
                    {bank.processing_note}
                </Banner>
            )}
        </>
    );

    if (step === 'submitted') {
        return (
            <>
                <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 4, textAlign: 'center' }}>
                    <CheckCircleIcon style={{ fontSize: 56, color: '#16A34A' }}/>
                    <SectionTitle sx={{ mt: 1 }}>Payment proof received</SectionTitle>
                    <Typography sx={{ fontSize: '0.9rem', color: MUTED, mt: 1, maxWidth: 520, mx: 'auto', lineHeight: 1.6 }}>
                        We will review your transfer for invoice <strong>{reference}</strong>. {receiveText ? `Your ${receiveText} will be added` : 'Your purchase will be applied'} once it is approved.
                        {bank.processing_note ? ` ${bank.processing_note}` : ''}
                    </Typography>
                </Box>
                <Footer left={needHelp}><Button variant="contained" onClick={onCancel} sx={primaryButtonSx}>Done</Button></Footer>
            </>
        );
    }

    if (step === 'proof') {
        return (
            <>
                <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 3 }}>
                    <TwoColumns
                        left={(
                            <>
                                <SectionTitle sx={{ mb: 1.5 }}>Upload payment proof</SectionTitle>
                                <Typography sx={{ fontSize: '0.85rem', color: MUTED, mb: 2, lineHeight: 1.55 }}>
                                    Upload the bank receipt or transfer confirmation for invoice <strong>{reference}</strong>. PDF, JPG or PNG, up to 10 MB.
                                </Typography>
                                <input ref={fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={handleFile}/>
                                <Box
                                    role="button" tabIndex={0}
                                    onClick={() => fileInput.current && fileInput.current.click()}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current && fileInput.current.click(); } }}
                                    sx={{
                                        p: 3, textAlign: 'center', borderRadius: '10px', cursor: 'pointer',
                                        border: `1.5px dashed ${fileError ? '#dc2626' : file ? ORANGE : 'var(--brand-border, #CBD5E1)'}`,
                                        background: file ? (isDark ? ORANGE_SOFT_DARK : ORANGE_SOFT) : 'var(--brand-bg, #F9FAFB)',
                                    }}
                                >
                                    <CloudUploadIcon style={{ fontSize: 34, color: ORANGE }}/>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mt: 0.5 }}>
                                        {file ? file.name : 'Choose a file'}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
                                        {file ? `${(file.size / 1024).toFixed(0)} KB — click to change` : 'PDF, JPG or PNG'}
                                    </Typography>
                                </Box>
                                {fileError && <Typography sx={{ mt: 1, fontSize: '0.8rem', color: '#dc2626' }}>{fileError}</Typography>}
                                <TextField
                                    fullWidth multiline minRows={2} size="small" sx={{ mt: 2 }}
                                    label="Note for DQ (optional)" value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))}
                                    placeholder="e.g. the transfer date or the account it was sent from"
                                />
                            </>
                        )}
                        right={summary}
                    />
                </Box>
                <Footer left={needHelp}>
                    <Button variant="outlined" onClick={() => (initialStep === 'proof' ? onCancel() : setStep('instructions'))} disabled={uploading} sx={secondaryButtonSx}>
                        {initialStep === 'proof' ? 'Cancel' : 'Back'}
                    </Button>
                    <Button variant="contained" onClick={handleSubmitProof} disabled={!file || uploading} sx={primaryButtonSx}>
                        {uploading ? <CircularProgress size={18} style={{ color: '#fff' }}/> : 'Submit proof'}
                    </Button>
                </Footer>
            </>
        );
    }

    return (
        <>
            <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 3 }}>
                <TwoColumns
                    left={(
                        <>
                            <SectionTitle sx={{ mb: 1.5 }}>Wire Transfer Instructions</SectionTitle>
                            <Banner sx={{ mb: 2 }}>Please include your invoice reference in the payment description so we can identify your payment.</Banner>
                            <BankRow label="Bank Name" value={bank.bank_name} note={bank.bank_name_note}/>
                            <BankRow label="Account Name" value={bank.account_name} note={bank.account_name_note}/>
                            <BankRow label="Account Number" value={bank.account_number} note={bank.account_number_note}/>
                            <BankRow label="SWIFT / BIC" value={bank.swift_bic} note={bank.swift_bic_note}/>
                            <BankRow label="Bank Code" value={bank.bank_code} note={bank.bank_code_note}/>
                            <BankRow label="Bank Address" value={bank.bank_address} note={bank.bank_address_note}/>
                            <BankRow label="Reference / Remark" value={`Invoice ${reference}`}/>
                            <BankRow label="Amount" value={fmtUsd(item.amount_usd)}/>
                            <Banner tone="blue" sx={{ mt: 2 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.25 }}>After making the payment</Typography>
                                Upload the payment proof on the next screen{bank.support_email ? <> or email it to <a href={`mailto:${bank.support_email}`} style={{ color: '#1D4ED8' }}>{bank.support_email}</a></> : ''} with your invoice number.
                            </Banner>
                        </>
                    )}
                    right={summary}
                />
            </Box>
            <Footer left={needHelp}>
                <Button variant="outlined" onClick={onCancel} sx={secondaryButtonSx}>Cancel</Button>
                <Button variant="contained" onClick={() => setStep('proof')} sx={primaryButtonSx}>I&apos;ve Made the Payment</Button>
            </Footer>
        </>
    );
};

// ─── Dialog ──────────────────────────────────────────────────────────────────

/**
 * @param {boolean}  open
 * @param {string}   publishableKey
 * @param {string}   clientSecret    from POST /dqin/billing/checkout/create
 * @param {object}   item            { label, amount_usd, dqt_amount, lines? }
 * @param {object}   payload         the purchase, as sent to checkout/create — reused for a bank transfer
 * @param {boolean}  walletsEnabled  from the checkout response
 * @param {Array}    savedCards      from the checkout response — the workspace's saved cards, primary first
 * @param {object}   paymentOptions  catalog.payment_options — { wire_transfer: { available } }
 * @param {string}   receiveLabel    what the buyer receives, e.g. "DQ Tokens"
 * @param {string}   fallbackEmail   used only if the Stripe customer has no email
 * @param {function} onPaid          (sessionId) => void
 * @param {function} onWireSubmitted (order) => void
 * @param {function} onClose
 */
const DqtCheckoutDialog = ({
    open, publishableKey, clientSecret, item, payload, walletsEnabled, savedCards = null, paymentOptions,
    receiveLabel = 'DQ Tokens', receiveText: receiveTextProp = null, fallbackEmail, onPaid, onWireSubmitted, onClose,
    createWire = null, uploadProof = null,
}) => {
    const [method, setMethod] = useState('card');
    const { isDark } = useColorMode();
    const stripePromise = useMemo(() => (publishableKey ? stripeFor(publishableKey) : null), [publishableKey]);
    // Stripe Elements renders in its own sandboxed iframe, so it can't resolve the
    // host page's `var(--pbdq-accent, ...)` — read the live value here instead.
    const options = useMemo(() => ({
        clientSecret,
        adaptivePricing: { allowed: true },
        elementsOptions: {
            // Saved cards are listed by CardPayment itself; keep the Payment Element
            // for a new card only, so a card never appears twice.
            savedPaymentMethod: { enableRedisplay: 'never' },
            // Stripe's own dark theme in dark mode, on the dialog's dark surface.
            appearance: isDark ? {
                theme: 'night',
                variables: { colorPrimary: resolveAccentColor(), borderRadius: '8px', fontSizeBase: '15px', colorBackground: '#242424', colorText: '#E5E7EB' },
                rules: { '.Label': { fontWeight: '500', color: '#9CA3AF' }, '.Input': { boxShadow: 'none', borderColor: 'rgba(255,255,255,0.18)' } },
            } : {
                theme: 'stripe',
                variables: { colorPrimary: resolveAccentColor(), borderRadius: '8px', fontSizeBase: '15px', colorText: '#111827' },
                rules: { '.Label': { fontWeight: '500', color: '#374151' }, '.Input': { boxShadow: 'none', borderColor: '#D1D5DB' } },
            },
        },
    }), [clientSecret, isDark]);

    useEffect(() => { if (open) setMethod('card'); }, [open, clientSecret]);

    if (!open || !clientSecret || !stripePromise) return null;

    const it = item || {};
    const wireAvailable = !!paymentOptions?.wire_transfer?.available && (!!payload || !!createWire);
    // What the buyer receives: DQ Tokens for a partner, or whatever the caller says (a
    // direct buyer sees licences and credits, never DQT).
    const receiveText = receiveTextProp || (it.dqt_amount ? fmtDqt(it.dqt_amount) : null);

    return (
        <Dialog
            open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body"
            sx={{ zIndex: 1400 }}
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', background: 'var(--brand-surface, #fff)' } }}
        >
            <Box sx={{ background: ORANGE, color: '#fff', px: { xs: 2, sm: 3.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Make a Payment</Typography>
                <IconButton onClick={onClose} aria-label="Close" sx={{ color: '#fff' }}><CloseIcon/></IconButton>
            </Box>

            <Box sx={{ background: ORANGE_SOFT, color: ORANGE_HOVER, px: { xs: 2, sm: 3.5 }, py: 1.5, display: 'flex', gap: 1.25, alignItems: 'center' }}>
                <InfoOutlinedIcon style={{ fontSize: 20 }}/>
                <Typography sx={{ fontSize: '0.88rem' }}>
                    Your {receiveLabel} will be updated after the payment is successfully processed.
                </Typography>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, pb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Box>
                        <SectionTitle>Amount</SectionTitle>
                        <Typography sx={{ fontSize: '0.82rem', color: MUTED, mt: 0.5 }}>
                            {it.label}{receiveText ? ` · you receive ${receiveText}` : ''}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.6rem' }, color: ORANGE, whiteSpace: 'nowrap' }}>{fmtUsd(it.amount_usd)}</Typography>
                </Box>

                <SectionTitle sx={{ mt: 2.5, mb: 1.5 }}>Choose a payment method</SectionTitle>
                <Box role="radiogroup" aria-label="Payment method" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pb: 2.5, borderBottom: `1px solid ${BORDER}`, mb: 2.5 }}>
                    <MethodCard
                        selected={method === 'card'} onSelect={() => setMethod('card')}
                        icon={<CreditCardIcon style={{ fontSize: 40 }}/>}
                        title="Credit / Debit Card" text="Pay securely using your credit or debit card."
                    >
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.75, ml: { xs: 0, sm: 11 }, flexWrap: 'wrap' }}>
                            <BrandBadge label="VISA" color="#1A1F71"/>
                            <BrandBadge label="MASTERCARD" color="#EB001B"/>
                            <BrandBadge label="AMEX" color="#2E77BC"/>
                            <BrandBadge label="DISCOVER" color="#E55C20"/>
                        </Box>
                    </MethodCard>
                    {wireAvailable && (
                        <MethodCard
                            selected={method === 'wire'} onSelect={() => setMethod('wire')}
                            icon={<AccountBalanceIcon style={{ fontSize: 40 }}/>}
                            title="Wire Transfer" text="Make a payment directly from your bank account."
                        />
                    )}
                </Box>
            </Box>

            {method === 'card' ? (
                <CheckoutElementsProvider stripe={stripePromise} options={options}>
                    <CardPayment
                        item={it} walletsEnabled={!!walletsEnabled} savedCards={savedCards || []} fallbackEmail={fallbackEmail}
                        receiveText={receiveText} onPaid={onPaid} onCancel={onClose}
                    />
                </CheckoutElementsProvider>
            ) : (
                <WirePayment
                    item={it} payload={payload} receiveText={receiveText} onCancel={onClose} onWireSubmitted={onWireSubmitted}
                    createWire={createWire} uploadProof={uploadProof}
                />
            )}
        </Dialog>
    );
};

/**
 * Upload proof later, for a bank transfer started earlier (listed in the purchase
 * panel). Opens straight at the proof step.
 *
 * @param {object}   order          a wire order from /wire/list
 * @param {object}   paymentOptions catalog.payment_options — its wire_transfer carries the help text
 */
export const DqtWireProofDialog = ({ open, order, paymentOptions, onWireSubmitted, onClose }) => {
    if (!open || !order) return null;
    const instructions = paymentOptions?.wire_transfer?.available ? paymentOptions.wire_transfer : {};
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body" sx={{ zIndex: 1400 }}
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', background: 'var(--brand-surface, #fff)' } }}>
            <Box sx={{ background: ORANGE, color: '#fff', px: { xs: 2, sm: 3.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Upload Payment Proof</Typography>
                <IconButton onClick={onClose} aria-label="Close" sx={{ color: '#fff' }}><CloseIcon/></IconButton>
            </Box>
            <Box sx={{ pt: 2.5 }}>
                <WirePayment
                    item={{ label: order.label, amount_usd: order.amount_usd, dqt_amount: order.dqt_amount, lines: order.lines || [] }}
                    receiveText={order.dqt_amount ? fmtDqt(order.dqt_amount) : null}
                    initialWire={{ order, instructions }} initialStep="proof"
                    onCancel={onClose} onWireSubmitted={onWireSubmitted}
                />
            </Box>
        </Dialog>
    );
};

export default DqtCheckoutDialog;
